import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import RaiseSection from "@/components/landing/RaiseSection";
import Footer from "@/components/landing/Footer";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_women-economy/artifacts/p7si2gi8_shescan%20logo%20mark%20and%20nameOrangeFinal.png";

export default function Investors() {
  useEffect(() => {
    document.title = "SheScan — Investor Relations";
  }, []);

  return (
    <main
      data-testid="investors-page"
      className="relative min-h-screen bg-white text-ink font-sans selection:bg-brand-orange selection:text-ink"
    >
      {/* Top nav — slimmed for the investor view */}
      <header className="border-b border-line bg-white sticky top-0 z-30 backdrop-blur-xl">
        <div className="w-full max-w-none px-4 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a
              href="/"
              data-testid="investors-logo"
              className="flex items-center gap-3 group"
            >
              <img
                src={LOGO_URL}
                alt="SheScan"
                className="h-14 w-auto object-contain"
              />
              <span className="font-sans text-[10px] sm:text-[11px] text-ink-soft tracking-widest font-semibold uppercase">
                Investor Relations
              </span>
            </a>
            <a
              href="/"
              data-testid="back-to-home"
              className="inline-flex items-center gap-2 border border-line px-4 py-2 sm:px-5 sm:py-2.5 font-mono uppercase text-[11px] tracking-[0.22em] text-ink hover:border-link transition-colors duration-150"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to site
            </a>
          </div>
        </div>
      </header>

      {/* Investor thesis hero */}
      <section
        data-testid="investors-hero"
        className="border-b border-line bg-white paper-grain"
      >
        <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
          <div className="flex items-center justify-between mb-10">
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
              Investor Relations · Pre-seed
            </span>
            <span className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
              Confidential · By invitation
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-8">
              <h1
                data-testid="investors-headline"
                className="font-display font-bold tracking-[-0.02em] leading-[0.94] text-ink text-5xl sm:text-6xl lg:text-7xl"
              >
                Infrastructure for the
                <br />
                <span className="text-brand-orange">$31.8&nbsp;trillion</span>
                <br />
                female economy.
              </h1>

              <div className="mt-10 space-y-6 text-lg sm:text-xl text-ink-soft leading-relaxed max-w-3xl">
                <p>
                  Women control roughly{" "}
                  <span className="text-ink font-mono">$31.8T</span> in global
                  consumer spending — yet ownership data is invisible at the
                  point of purchase. SheScan is the consumer-facing layer that
                  closes the loop between intent and action.
                </p>
                <p>
                  We're raising pre-seed capital to build the verified
                  Gender-Identity Metadata architecture, ship the iOS scanner,
                  and launch in North America. The thesis is short:{" "}
                  <span className="text-ink">
                    we're not building a campaign. We're building infrastructure.
                    Infrastructure compounds.
                  </span>
                </p>
              </div>
            </div>

            <aside
              className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-line"
              data-testid="investors-sidecard"
            >
              <div className="flex items-center justify-between pb-3 border-b border-line">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink">
                  At a glance
                </span>
                <span className="font-mono text-[10px] text-ink-soft">
                  2025
                </span>
              </div>

              <dl className="mt-6 space-y-6">
                <Row label="Stage" value="Pre-seed" />
                <Row label="Round size" value="$600K" accent />
                <Row label="Pre-money" value="$5.0M" accent />
                <Row label="Markets · 5 yr" value="NA · LATAM · EU · East Africa" />
                <Row label="Velocity goal" value="1× → 6–8×" />
              </dl>

              <p className="mt-8 pt-6 border-t border-line text-sm text-ink-soft leading-relaxed">
                Detailed terms, use of funds, and the data room are gated below.
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* Reuse the existing gated raise component */}
      <RaiseSection />

      <Footer />
    </main>
  );
}

function Row({ label, value, accent }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
        {label}
      </dt>
      <dd
        className={`font-mono mt-1 tracking-tight ${
          accent ? "text-brand-orange text-3xl sm:text-4xl" : "text-ink text-base"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
